import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { refundOrder } from '../apiEndpoints';
import useIsMobile from '../hooks/useIsMobile';
import { currencySymbol } from '@/utils/currency';
import { btnGhost, input as inputCls, label as labelCls } from '@/components/ui/design';
import { Select, SelectItem } from '@/components/ui/AppSelect';

interface RefundModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: any;
    eventId: string;
}

export default function RefundModal({ isOpen, onClose, order, eventId }: RefundModalProps) {
    const [refundReason, setRefundReason] = useState('');
    const [refundNotes, setRefundNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const queryClient = useQueryClient();

    const refundMutation = useMutation({
        mutationFn: (refundData: { reason: string; refundAmount?: number; refundNotes?: string }) =>
            refundOrder(order._id, refundData),
        onSuccess: () => {
            // Refetch orders to update the UI
            queryClient.invalidateQueries({ queryKey: ['eventOrders', eventId] });
            queryClient.invalidateQueries({ queryKey: ['orders', eventId] });
            onClose();
            setRefundReason('');
            setRefundNotes('');
        },
        onError: (error) => {
            console.error('Refund failed:', error);
            setIsSubmitting(false);
        }
    });

    const handleRefund = async () => {
        if (!refundReason.trim()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await refundMutation.mutateAsync({
                reason: refundReason.trim(),
                refundAmount: order.totalAmount,
                refundNotes: refundNotes.trim() || undefined
            });
        } catch (error) {
            console.error('Refund failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isMobile = useIsMobile();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'md'}
            scrollBehavior="inside"
            isDismissable={!isSubmitting}
            classNames={{
                base: 'bg-white dark:bg-nightSurface rounded-none md:rounded-[28px] shadow-none md:ring-1 md:ring-black/[0.06] md:dark:ring-white/10 h-full md:h-auto',
                wrapper: 'p-0 md:p-4 z-[100000]',
                header: 'px-6 pt-6',
                body: 'px-6',
                footer: 'px-6 pb-6',
                closeButton: 'right-4 top-4 rounded-full hover:bg-grey99 dark:hover:bg-white/10',
                backdrop: 'bg-black/50',
            }}
        >
            <ModalContent>
                <ModalHeader>
                    <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Confirm Refund</h2>
                </ModalHeader>

                <ModalBody className="py-4">
                    {/* Order Information */}
                    <div className="mb-6 rounded-[28px] bg-grey99 p-5 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                        <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted mb-4">Order Details</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between gap-4">
                                <span className="shrink-0 text-grey40 dark:text-nightMuted">Order ID:</span>
                                <span className="min-w-0 break-all text-right text-grey10 dark:text-white font-semibold">{order.hashedUniqueId || order.orderId}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="shrink-0 text-grey40 dark:text-nightMuted">Customer:</span>
                                <span className="min-w-0 break-all text-right text-grey10 dark:text-white font-semibold">
                                    {order.customerName || order.guestName || order.attendeeName || 'N/A'}
                                </span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="shrink-0 text-grey40 dark:text-nightMuted">Amount:</span>
                                <span className="text-grey10 dark:text-white font-bold">{currencySymbol(order?.currency)}{order.totalAmount}</span>
                            </div>
                            <div className="flex justify-between gap-4">
                                <span className="shrink-0 text-grey40 dark:text-nightMuted">Payment Method:</span>
                                <span className="min-w-0 break-all text-right text-grey10 dark:text-white font-semibold capitalize">
                                    {order.paymentMethod || 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Refund Form */}
                    <div className="mb-6">
                        <div className="mb-4">
                            <label className={labelCls}>
                                Refund Reason <span className="text-error50">*</span>
                            </label>
                            <Select aria-label="Select" selectedKeys={[String(refundReason ?? '')]} onChange={(e) => setRefundReason(e.target.value)} isDisabled={isSubmitting}>
                                <SelectItem key="">Select a reason</SelectItem>
                                <SelectItem key="customer_request">Customer Request</SelectItem>
                                <SelectItem key="event_cancelled">Event Cancelled</SelectItem>
                                <SelectItem key="event_postponed">Event Postponed</SelectItem>
                                <SelectItem key="technical_issue">Technical Issue</SelectItem>
                                <SelectItem key="duplicate_payment">Duplicate Payment</SelectItem>
                                <SelectItem key="other">Other</SelectItem>
                            </Select>
                        </div>

                        <div className="mb-4">
                            <label className={labelCls}>
                                Additional Notes (Optional)
                            </label>
                            <textarea
                                value={refundNotes}
                                onChange={(e) => setRefundNotes(e.target.value)}
                                className={`${inputCls} h-auto py-3 resize-none`}
                                rows={3}
                                placeholder="Provide additional details about the refund..."
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Warning Message */}
                    <div className="mb-2 rounded-2xl bg-yellow-50 p-4 ring-1 ring-yellow-200 dark:bg-yellow-500/10 dark:ring-yellow-500/20">
                        <div className="flex items-start gap-3">
                            <svg width="20" height="20" fill="none" viewBox="0 0 20 20" className="shrink-0 text-yellow-600 dark:text-yellow-400 mt-0.5">
                                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" fill="currentColor" />
                            </svg>
                            <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                <p className="font-bold mb-1">Refund Warning</p>
                                <p>This action will refund the full amount of {currencySymbol(order?.currency)}{order.totalAmount} to the customer. This action cannot be undone.</p>
                            </div>
                        </div>
                    </div>
                </ModalBody>

                <ModalFooter className="flex flex-col sm:flex-row gap-3">
                    <Button variant="light" onPress={onClose} className={`${btnGhost} w-full sm:w-auto bg-transparent`} isDisabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onPress={handleRefund}
                        isLoading={isSubmitting}
                        isDisabled={!refundReason.trim()}
                        className="inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-error50 px-6 text-sm font-semibold text-white transition-colors hover:bg-error40"
                    >
                        Confirm Refund
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}